
import { useEffect, useState } from "react";
import { Col, Form, FormGroup, Label, Row } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Btn, H4, P } from "../../../AbstractElements";
import CommonLogo from "./CommonLogo";
import organizationData from "../../../utils/organizationData.json";
import UserListData from "../../../utils/UserListData.json";
import { formatOptionLabelWithFlag } from "../../users/organization-admin/edit-user-list/UserListInfoSection";
import { showWarningAlert } from "../../../utils/showWarningAlert";

type OptionType = {
  value: string;
  label: string;
  flag?: string;
  symbol?: string;
};

interface CommonFormPropsType {
  alignLogo?: string;
}

interface FormValues {
  firstName: string;
  lastName: string;
  organizationName: string;
  country: string;
  city: string;
  countryCode: string;
  phoneNumber: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const CommonRegisterForm = ({ alignLogo }: CommonFormPropsType) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [countryOptions, setCountryOptions] = useState<OptionType[]>([]);
  const [cityOptions, setCityOptions] = useState<OptionType[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<OptionType | null>(null);
  const [selectedCity, setSelectedCity] = useState<OptionType | null>(null);
  const [selectedCountryCode, setSelectedCountryCode] = useState<OptionType | null>(null);

  // Load country options
  useEffect(() => {
    const countries = organizationData.countries
      .filter((country) => typeof country.label === "string" && country.label.trim() !== "")
      .map((country) => ({
        value: country.value,
        label: country.label,
        flag: country.flag,
      }));
    setCountryOptions(countries);
  }, []);

  // Update city options based on selected country
  useEffect(() => {
    if (selectedCountry) {
      const cities =
        organizationData.cities[
          selectedCountry.value as keyof typeof organizationData.cities
        ]?.filter((city) => typeof city.label === "string" && city.label.trim() !== "") || [];
      setCityOptions(cities);
    } else {
      setCityOptions([]);
    }
  }, [selectedCountry]);

  // Validation schema using Yup
  const validationSchema = Yup.object({
    firstName: Yup.string().required(t("FirstNameRequired")),
    lastName: Yup.string().required(t("LastNameRequired")),
    organizationName: Yup.string().required(t("OrganizationNameRequired")),
    country: Yup.string().required(t("CountryRequired")),
    city: Yup.string().required(t("CityRequired")),
    countryCode: Yup.string().required(t("CountryCodeRequired")),
    phoneNumber: Yup.string()
      .matches(/^[0-9]{10}$/, t("PhoneNumberInvalid"))
      .required(t("PhoneNumberRequired")),
    email: Yup.string().email(t("EmailInvalid")).required(t("EmailRequired")),
    password: Yup.string()
      .min(8, t("PasswordMinLength"))
      .required(t("PasswordRequired")),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password")], t("ConfirmPasswordMatch"))
      .required(t("ConfirmPasswordRequired")),
  });

  // Initial form values
  const initialValues: FormValues = {
    firstName: "",
    lastName: "",
    organizationName: "",
    country: "",
    city: "",
    countryCode: "",
    phoneNumber: "",
    email: "",
    password: "",
    confirmPassword: "",
  };


  return (
    <div className="login-card login-dark">
      <div>
        <div>
          <CommonLogo alignLogo={alignLogo} />
        </div>
        <div className="login-main">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
              console.log("Form submitted with values:", values);
              try {
                 showWarningAlert({
                  icon: "success",
                  title: t("RegistrationSuccessTitle"),
                  text: t("PleaseCheckYourEmail"),
                  confirmButtonColor: "var(--theme-default)",
                  onConfirm: () => {
                   
                    navigate(`${process.env.PUBLIC_URL}/login`);
                  },
                });
                console.log("showWarningAlert called successfully");
               
              } catch (error) {
                console.error("Error in showWarningAlert:", error);
              }
              setSubmitting(false);
         
            }}
          >
            {({ setFieldValue, values, handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <H4>{t("CreateYourAccount")}</H4>
                <P className="mt-1">{t("Enter your details to create an account")}</P>

                <FormGroup>
                  <Label className="col-form-label pt-0">
                    {t("OrganizationContactPerson")}  <span className="txt-danger">*</span>   
                  </Label> 
                  <Row className="g-2">
                    <Col xs="6">
                      <Field
                        name="firstName"
                        type="text"
                        className="form-control"
                        placeholder={t("FirstName")}
                      />
                      <ErrorMessage
                        name="firstName"
                        component="span"
                        className="text-danger"
                      />
                    </Col>
                    <Col xs="6">
                      <Field
                        name="lastName"
                        type="text"
                        className="form-control"
                        placeholder={t("LastName")}
                      />
                      <ErrorMessage
                        name="lastName"
                        component="span"
                        className="text-danger"
                      />
                    </Col>
                  </Row>
                </FormGroup>

                <FormGroup>
                  <Label className="col-form-label">{t("OrganizationName")}  <span className="txt-danger">*</span></Label>
                  <Field
                    name="organizationName"
                    type="text"
                    className="form-control"
                    placeholder={t("OrganizationNamePlaceholder")}
                  />
                  <ErrorMessage
                    name="organizationName"
                    component="span"
                    className="text-danger"
                  />
                </FormGroup>

                <Row>
                  <Col sm="6">
                    <FormGroup>
                      <Label>
                        {t("OrganizationCountry")}{" "}
                        <span className="txt-danger">*</span>
                      </Label>
                      <Select
                        name="country"
                        options={countryOptions}
                        value={countryOptions.find((opt) => opt.label === values.country) || null}
                        onChange={(option) => {
                          setSelectedCountry(option);
                          setFieldValue("country", option ? option.label : "");
                          setSelectedCity(null);
                          setFieldValue("city", "");
                        }}
                        formatOptionLabel={formatOptionLabelWithFlag}
                        placeholder={t("SelectCountry")}
                        isClearable
                        classNamePrefix="select"
                        className="basic-single-select"
                      />
                      <ErrorMessage
                        name="country"
                        component="span"
                        className="text-danger"
                      />
                    </FormGroup>
                  </Col>
                  <Col sm="6">
                    <FormGroup>
                      <Label>
                        {t("OrganizationCity")}{" "}
                        <span className="txt-danger">*</span>
                      </Label>
                      <Select
                        name="city"
                        options={cityOptions}
                        value={cityOptions.find((opt) => opt.label === values.city) || null}
                        onChange={(option) => {
                          setSelectedCity(option);
                          setFieldValue("city", option ? option.label : "");
                        }}
                        placeholder={t("OrganizationCityPlaceholder")}
                        isDisabled={!selectedCountry}
                        isClearable
                        classNamePrefix="select"
                        className="basic-single-select"
                      />
                      <ErrorMessage
                        name="city"
                        component="span"
                        className="text-danger"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup>
                  <Label className="col-form-label">{t("EnterMobileNumber")}  <span className="txt-danger">*</span></Label>
                  <Row className="g-2"> 
                    <Col xs="4">
                      <Select
                        name="countryCode"
                        options={UserListData.countrycode}
                        value={UserListData.countrycode.find((opt) => opt.value === values.countryCode) || null}
                        onChange={(option) => {
                          setSelectedCountryCode(option);
                          setFieldValue("countryCode", option ? option.value : "");
                        }}
                        placeholder="+91"
                        className="basic-single-select"
                        classNamePrefix="select"
                        isSearchable={true}
                        formatOptionLabel={formatOptionLabelWithFlag}
                      />
                      <ErrorMessage
                        name="countryCode"
                        component="span"
                        className="text-danger"
                      />
                    </Col>
                    <Col xs="8">
                      <Field
                        name="phoneNumber"
                        type="tel"
                        className="form-control"
                        placeholder={t("EnterMobileNumber")}
                      />
                      <ErrorMessage
                        name="phoneNumber"
                        component="span"
                        className="text-danger"
                      />
                    </Col>
                  </Row>
                </FormGroup>

                <FormGroup>
                  <Label className="col-form-label">{t("EmailAddress")}  <span className="txt-danger">*</span></Label>
                  <Field
                    name="email"
                    type="email"
                    className="form-control"
                    placeholder={t("OrganizationEmailPlaceholder")}
                  />
                  <ErrorMessage
                    name="email"
                    component="span"
                    className="text-danger"
                  />
                </FormGroup>
                <FormGroup>
                <Label check>
                {"Password"} <span className="txt-danger">*</span>
              </Label>
              <Field
                className="form-control"
                name="password"
                type="password"
                placeholder={t("EnterPassword")}
                autoComplete="new-password"
              />
              <ErrorMessage
                name="password"
                component="span"
                className="text-danger"
              />
                </FormGroup>

                <FormGroup>
                <Label check>
                {"ConfirmPassword"} <span className="txt-danger">*</span>
              </Label>
              <Field
                className="form-control"
                name="confirmPassword"
                type="password"
                placeholder={t("EnterConfirmPassword")}
                autoComplete="new-password"
              />
              <ErrorMessage
                name="confirmPassword"
                component="span"
                className="text-danger"
              />
         
           
                </FormGroup>

                <FormGroup className="mb-0">
                  <Btn block color="primary" type="submit" className="w-100">
                    {t("Register")}
                  </Btn>
                </FormGroup>

                <P className="mt-4 mb-0 text-center">
                  {t("Already have an account?")}
                  <Link className="ms-2" to={`${process.env.PUBLIC_URL}/Login`}>
                    {t("SignIn")}
                  </Link>
                </P>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default CommonRegisterForm;
